import { Columns03Icon } from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';
import * as Popover from '@radix-ui/react-popover';
import { Table } from '@tanstack/react-table';

import { Fragment, useMemo } from 'react';

import { Button, IconButton } from '@/design-system';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

export function DataGridColumnVisibilityPopover<TData>(props: {
  table: Table<TData>;
  onResetClick?: () => void;
  localStorageKey?: `ls:${string}`;
  size?: 'sm' | 'md';
}) {
  const [, setColumnVisibility] = useLocalStorageState<Record<string, boolean>>(
    props.localStorageKey ?? null,
    {}
  );

  const allColumnsVisible = useMemo(
    () =>
      props.table.getAllLeafColumns().every((column) => column.getIsVisible()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.table.getState().columnVisibility]
  );

  const anyColumnsVisible = useMemo(
    () =>
      props.table
        .getAllLeafColumns()
        .some((column) => column.getIsVisible() && column.getCanHide()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.table.getState().columnVisibility]
  );

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <div className="flex items-center gap-2">
          {props.size === 'sm' ? (
            <IconButton
              icon={Columns03Icon}
              label="Columns"
              color="secondary"
              variant="outlined"
              size="sm"
            />
          ) : (
            <Button
              color="secondary"
              variant="outlined"
              size="md"
              leftDecorator={Columns03Icon}
            >
              Columns
            </Button>
          )}
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content sideOffset={5} className="relative z-10">
          <div className="flex flex-col rounded-md border border-secondary bg-popover shadow-lg">
            <div className="flex flex-col">
              <div className="px-4 pb-2 pt-4">
                <Checkbox
                  checked={allColumnsVisible}
                  indeterminate={anyColumnsVisible && !allColumnsVisible}
                  onChange={() => {
                    const toSetVisibility = !allColumnsVisible;
                    props.table.toggleAllColumnsVisible(toSetVisibility);
                    if (props.localStorageKey) {
                      setColumnVisibility((prev) => ({
                        ...Object.fromEntries(
                          Object.keys(prev).map((key) => [key, toSetVisibility])
                        ),
                      }));
                    }
                  }}
                  label={'Show all columns'}
                />
              </div>
              <hr className="flex-shrink-0 border-secondary" />
            </div>
            <div className="flex max-h-[400px] max-w-[420px] flex-col gap-2 overflow-auto p-4 ">
              {props.table
                .getAllLeafColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  const { name: alternativeName } = (column.columnDef.meta ??
                    {}) as { name?: string };

                  return (
                    <Checkbox
                      key={column.id}
                      checked={column.getIsVisible()}
                      onChange={(e) => {
                        column.toggleVisibility(e.target.checked);
                        if (props.localStorageKey) {
                          setColumnVisibility((prev) => ({
                            ...prev,
                            [column.id]: e.target.checked,
                          }));
                        }
                      }}
                      label={
                        typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : alternativeName ?? column.id
                      }
                    />
                  );
                })}
            </div>

            {props.onResetClick && (
              <Fragment>
                <hr className="flex-shrink-0 border-secondary" />

                <button
                  type="button"
                  className="mx-2 my-2 rounded-md px-2 py-1 text-left text-sm text-tertiary transition-all hover:bg-secondary-hover active:bg-secondary"
                  onClick={props.onResetClick}
                >
                  Reset to default
                </button>
              </Fragment>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
